import { useSearchParams } from "react-router";
import { AdminDataNotice } from "@/components/admin/data-notice";
import { LogFilters } from "@/components/admin/log-filters";
import { RefreshButton } from "@/components/admin/refresh-button";
import { useAdminView } from "@shared/client/admin-view";
import { usePageTitle } from "@shared/client/view";
import { AdminChip, AdminEmpty, AdminPanel, StatCard, StatGroup } from "@shared/components/blocks";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@shared/components/ui/table";
import type { LogRecord } from "@shared/lib/admin-db";
import { formatDateTime, formatNumber } from "@shared/lib/format";

const SOURCE_LABELS: Record<string, string> = {
  api: "API",
  db: "Base de données",
  auth: "Auth",
  external: "Externe",
  app: "Application",
};

export default function AdminLogsPage() {
  usePageTitle("Logs — Administration Hune");
  const [params] = useSearchParams();
  const level = params.get("level") ?? "all";
  const source = params.get("source") ?? "all";
  const q = params.get("q") ?? "";
  const { data, error: loadError } = useAdminView<{ logs: LogRecord[] }>("/api/views/admin/logs");
  if (!data && !loadError) return null;
  const logs = data?.logs ?? [];

  const errorCount = logs.filter((log) => log.level === "error").length;
  const warnCount = logs.filter((log) => log.level === "warn").length;
  const durations = logs.filter((log) => log.duration_ms !== null);
  const avgDuration = durations.length
    ? Math.round(durations.reduce((sum, log) => sum + (log.duration_ms ?? 0), 0) / durations.length)
    : null;

  return (
    <>
      <StatGroup className="mb-4.5" columns={4}>
        <StatCard label="Entrées affichées" value={formatNumber(logs.length)} />
        <StatCard label="Erreurs" tint={errorCount > 0 ? "rose" : "blue"} value={formatNumber(errorCount)} />
        <StatCard label="Avertissements" tint={warnCount > 0 ? "amber" : "blue"} value={formatNumber(warnCount)} />
        <StatCard label="Durée moyenne" value={avgDuration === null ? "—" : `${formatNumber(avgDuration)} ms`} />
      </StatGroup>

      <AdminPanel flush>
        <div className="flex flex-wrap items-center gap-3 border-b px-4 py-3">
          <LogFilters level={level} q={q} source={source} />
          <RefreshButton />
        </div>

        {loadError ? (
          <div className="p-4"><AdminDataNotice error={loadError} /></div>
        ) : logs.length === 0 ? (
          <AdminEmpty>Aucune entrée ne correspond à ces filtres.</AdminEmpty>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-5">Quand</TableHead>
                <TableHead>Niveau</TableHead>
                <TableHead className="max-sm:hidden">Source</TableHead>
                <TableHead className="max-sm:pr-4">Opération</TableHead>
                <TableHead className="max-sm:hidden">Statut</TableHead>
                <TableHead className="max-sm:hidden">Durée</TableHead>
                <TableHead className="pr-5 max-sm:hidden">Détail</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log) => (
                <TableRow
                  className={
                    log.level === "error" ? "bg-destructive/4" : log.level === "warn" ? "bg-chart-4/6" : undefined
                  }
                  key={log.id}
                >
                  <TableCell className="pl-5 whitespace-nowrap text-muted-foreground tabular-nums">
                    {formatDateTime(log.created_at)}
                  </TableCell>
                  <TableCell>
                    <AdminChip tone={log.level === "error" ? "churned" : log.level === "warn" ? "trial" : "neutral"}>
                      {log.level}
                    </AdminChip>
                  </TableCell>
                  <TableCell className="max-sm:hidden">{SOURCE_LABELS[log.source] ?? log.source}</TableCell>
                  <TableCell className="max-sm:pr-4 max-sm:whitespace-normal">
                    <code className="font-mono text-[12px]">{log.operation}</code>
                    {log.path && (
                      <small className="block text-[12px] text-muted-foreground max-sm:break-all">{log.method} {log.path}</small>
                    )}
                    {/* Mobile : statut et erreur ici, leurs colonnes sont masquées. */}
                    {(log.error || (log.status ?? 0) >= 400) && (
                      <small className="block text-[12px] text-destructive sm:hidden">
                        {log.status && log.status >= 400 ? `HTTP ${log.status}` : ""}
                        {log.error ? ` ${log.error}` : ""}
                      </small>
                    )}
                  </TableCell>
                  <TableCell className="tabular-nums max-sm:hidden">{log.status ?? "—"}</TableCell>
                  <TableCell className="whitespace-nowrap tabular-nums max-sm:hidden">
                    {log.duration_ms === null ? "—" : `${formatNumber(log.duration_ms)} ms`}
                  </TableCell>
                  <TableCell className="pr-5 text-destructive max-sm:hidden">
                    {log.error || "—"}
                    {log.request_id && (
                      <small className="block text-[12px] text-muted-foreground">req {log.request_id.slice(0, 8)}</small>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </AdminPanel>
    </>
  );
}
